<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { VuePDF, usePDF } from '@tato30/vue-pdf'
  import { CasesService } from '@/types/generated/services.gen'
  import {
    type CustomerCase,
    type CustomerDataMapper as DataMapper
  } from '@/types/generated/types.gen'
  import router from '@/router'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import { Switch } from '@/components/ui/switch'
  import { DocumentTextIcon } from '@heroicons/vue/24/outline'
  import '@tato30/vue-pdf/style.css'
  import SearchIcon from '@/components/icons/SearchIcon.vue'
  import TEMPORARYCaseBaseComponent from '@/components/app/cases/TEMPORARYCaseBaseComponent.vue'
  import { ROUTES } from '@/router/routes'
  import TEMPORARYEntityRow from '@/components/app/cases/TEMPORARYEntityRow.vue'
  import TEMPORARYMapperSourceTableCell from '@/components/app/cases/TEMPORARYMapperSourceTableCell.vue'
  import TEMPORARYMapperDataTableCell from '@/components/app/cases/TEMPORARYMapperDataTableCell.vue'
  import SkeletonLoaderTable from '@/components/skeleton/SkeletonLoaderTable.vue'

  const caseData = ref<CustomerCase | null>(null)
  const mappers = ref<DataMapper[]>([])
  const showMappersWithValues = ref(true)
  const showMappersWithValuesEntity = ref(true)
  const search = ref('')
  const searchEntity = ref('')
  const filteredMappers = computed<DataMapper[]>(() => {
    return mappers.value?.filter((mapper) => {
      if (showMappersWithValues.value) {
        return (
          mapper.data &&
          mapper.label.toString().toLowerCase().includes(search.value.toLowerCase()) &&
          mapper.is_law_specific_field &&
          mapper.law_field === 'Arbeitsrecht'
        )
      }
      return (
        mapper.label.toLowerCase().includes(search.value.toLowerCase()) &&
        mapper.is_law_specific_field &&
        mapper.law_field === 'Arbeitsrecht'
      )
    })
  })

  const showAssistant = ref(false)
  onMounted(async () => {
    caseData.value = (await CasesService.casesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as CustomerCase
    mappers.value = (
      await CasesService.casesDatamappersList({
        uuid: router.currentRoute.value.params.uuid.toString()
      })
    ).results as DataMapper[]
  })

  const handleSwitchChange = (value: boolean) => {
    showMappersWithValues.value = value
  }

  const handleSwitchChangeEntity = (value: boolean) => {
    showMappersWithValuesEntity.value = value
  }

  const entityUuids = computed<string[]>(() => {
    const uuids: string[] = []
    mappers.value.forEach((mapper) => {
      if (mapper.is_entity_field && uuids.indexOf(mapper.entity_uuid as string) === -1) {
        uuids.push(mapper.entity_uuid as string)
      }
    })
    return uuids
  })

  const mainContentClasses = computed(() => ({
    'pr-[480px]': showAssistant.value,
    'transition-all duration-300 ease-in-out': true
  }))
</script>

<template>
  <div
    class="h-full w-full"
    :class="mainContentClasses"
  >
    <TEMPORARYCaseBaseComponent
      :mappers="mappers"
      :case-data="caseData"
    >
      <div class="mt-4 flex items-center space-x-2">
        <a
          :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseData.uuid)}`"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
        >
          <DocumentTextIcon class="h-5 w-5" />
          Dokument generieren
          <span class="badge bg-purple">KI</span>
        </a>
      </div>

      <div
        class="mt-4 flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700
          dark:bg-neutral-900 dark:text-neutral-400 md:p-5"
      >
        <div>
          <div class="grid gap-y-2 pb-5 ps-1 pt-1 md:grid-cols-2 md:gap-x-5 md:gap-y-0">
            <div>
              <!-- Search Input -->
              <div class="relative">
                <div
                  class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
                >
                  <SearchIcon class="size-4 flex-shrink-0 text-gray-500" />
                </div>
                <input
                  v-model="search"
                  class="block w-full rounded-lg border-transparent bg-gray-100 px-3 py-[7px] ps-10 text-sm
                    focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
                  type="text"
                  :placeholder="'Suche nach Feld'"
                />
              </div>
              <!-- End Search Input -->
            </div>
            <div
              class="flex items-center justify-between"
              :class="showMappersWithValues ? '' : ''"
            >
              <div></div>
              <div class="flex items-center space-x-4">
                <div class="flex items-center space-x-2">
                  <label class="text-sm text-gray-700">Nur Felder mit Werten anzeigen</label>
                  <Switch
                    v-model="showMappersWithValues"
                    :checked="showMappersWithValues"
                    class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
                    thumb-class=""
                    @update:checked="handleSwitchChange"
                  />
                </div>
              </div>
            </div>
            <!-- End Col -->
          </div>
          <Table v-if="mappers.length > 0">
            <TableHeader>
              <TableRow>
                <TableCell>Rechtsgebiet</TableCell>
                <TableCell class="w-1/3">Feld</TableCell>
                <TableCell class="w-1/3">Wert</TableCell>
                <TableCell class="w-1/3">Quelle</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                v-for="mapper in filteredMappers"
                :key="mapper.key"
              >
                <TableCell>{{ mapper.law_field }}</TableCell>
                <TableCell class="w-1/3">{{ mapper.label }}</TableCell>
                <TEMPORARYMapperDataTableCell
                  class="w-1/3"
                  :mapper="mapper"
                />
                <TEMPORARYMapperSourceTableCell
                  class="w-1/3"
                  :mapper="mapper"
                />
              </TableRow>
            </TableBody>
          </Table>
          <SkeletonLoaderTable
            v-else
            :columns="4"
            :rows="5"
          />
        </div>
      </div>

      <h2 class="pt-6 text-xl font-bold text-gray-900">Beteiligte</h2>

      <div
        class="mt-4 flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700
          dark:bg-neutral-900 dark:text-neutral-400 md:p-5"
      >
        <div>
          <div class="grid gap-y-2 pb-5 ps-1 pt-1 md:grid-cols-2 md:gap-x-5 md:gap-y-0">
            <div>
              <!-- Search Input -->
              <div class="relative">
                <div
                  class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
                >
                  <SearchIcon class="size-4 flex-shrink-0 text-gray-500" />
                </div>
                <input
                  v-model="searchEntity"
                  class="block w-full rounded-lg border-transparent bg-gray-100 px-3 py-[7px] ps-10 text-sm
                    focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
                  type="text"
                  :placeholder="'Suche nach Feld'"
                />
              </div>
              <!-- End Search Input -->
            </div>
            <div
              class="flex items-center justify-between"
              :class="showMappersWithValuesEntity ? '' : ''"
            >
              <div></div>
              <div class="flex items-center space-x-4">
                <div class="flex items-center space-x-2">
                  <label class="text-sm text-gray-700">Nur Felder mit Werten anzeigen</label>
                  <Switch
                    v-model="showMappersWithValuesEntity"
                    :checked="showMappersWithValuesEntity"
                    class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
                    thumb-class=""
                    @update:checked="handleSwitchChangeEntity"
                  />
                </div>
              </div>
            </div>
            <!-- End Col -->
          </div>
          <Table v-if="mappers.length > 0">
            <TableHeader>
              <TableRow>
                <TableCell>Beteiligter</TableCell>
                <TableCell>Feld</TableCell>
                <TableCell>Wert</TableCell>
                <TableCell>Quelle</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TEMPORARYEntityRow
                v-for="(uuid, index) in entityUuids"
                :mappers="mappers"
                :entity-uuid="uuid"
                :key="index"
                :show-mappers-with-values="showMappersWithValuesEntity"
                :search="searchEntity"
              />
            </TableBody>
          </Table>
          <SkeletonLoaderTable
            v-else
            :columns="4"
            :rows="5"
          />
        </div>
      </div>
    </TEMPORARYCaseBaseComponent>
  </div>
</template>
